import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { RenderIf } from 'config';
import { Icon, Text, Button } from 'components';
import { Tag } from '@blueprintjs/core';
import { isEmpty, length, equals } from 'ramda';

const MatchesSearchBox = ({ loading, onSubmit, matchStatuses }) => {
  const defaultError = { numeric: false, maxReached: false };
  const [matchIDs, setMatchIDs] = useState([]);
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState(defaultError);
  const searchInput = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const MAX_MATCHES = 10;
  const REGEX = /[\n,\s]+/;
  const NON_NUMERIC_REGEX = /^\d*$/;
  const availableSlots = MAX_MATCHES - matchIDs.length;

  const getPlaceholderStr = () => {
    if (isEmpty(matchIDs)) return 'Search by matchIDs..';
    if (availableSlots > 0) return 'Add MatchID..';
    return `Maximum of ${MAX_MATCHES} match IDs reached`;
  };

  const processAndAddIds = (inputValue) => {
    const ids = inputValue.split(REGEX).filter((id) => id.trim() !== '');
    if (equals(length(ids), 0)) return { newMatchIDs: matchIDs };

    const validIds = ids
      .map((id) => id.trim())
      .filter(
        (id) =>
          id.length <= 12 &&
          id !== '' &&
          NON_NUMERIC_REGEX.test(id) &&
          !matchIDs.includes(id)
      );
    const idsToAdd = validIds.slice(0, availableSlots);

    // Check for errors
    const hasNonNumeric = ids.some((id) => !NON_NUMERIC_REGEX.test(id.trim()));
    if (hasNonNumeric) {
      setErrors({ ...errors, numeric: true });
    } else if (validIds.length > availableSlots) {
      setErrors({ ...errors, maxReached: true });
    } else {
      setErrors(defaultError);
    }
    const newMatchIDs =
      idsToAdd.length > 0 ? [...matchIDs, ...idsToAdd] : matchIDs;
    if (idsToAdd.length > 0) {
      setMatchIDs(newMatchIDs);
    }
    return { newMatchIDs };
  };

  // Focus input when loading finishes
  useEffect(() => {
    if (!loading) searchInput.current?.focus();
  }, [loading]);

  const checkStatus = (id) => {
    const numID = Number(id);
    const { missing, found } = matchStatuses;
    if (missing?.includes(numID)) return 'warning';
    if (found?.includes(numID)) return 'success';
    return 'primary';
  };

  // Function to remove tag from array
  const handleRemoveMatch = (id) => {
    setMatchIDs(matchIDs.filter((t) => t !== id));
    setErrors({ errors, maxReached: false });
    searchInput.current?.focus();
  };

  const onInputChange = (newValue) => {
    // Check if the input contains non-numeric characters
    if (newValue && !NON_NUMERIC_REGEX.test(newValue)) {
      setErrors({ ...errors, numeric: true });
    } else {
      setErrors(defaultError);
    }
    setValue(newValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === ' ' || e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const { newMatchIDs } = processAndAddIds(value);
      setValue('');
      if (e.key === 'Enter') onSubmit(newMatchIDs);
      searchInput.current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    processAndAddIds(pastedText);
  };

  const handleSearchClick = () => {
    let finalMatchIDs = matchIDs;
    if (value.trim() !== '') {
      const { newMatchIDs } = processAndAddIds(value);
      finalMatchIDs = newMatchIDs;
      setValue('');
      searchInput.current?.focus();
    }
    onSubmit(finalMatchIDs);
  };

  const onClear = () => {
    setMatchIDs([]);
    setErrors({ defaultError });
    onSubmit([]);
    searchInput.current?.focus();
  };

  return (
    <div
      className={classNames('matches-search-box', {
        left: !isEmpty(matchStatuses.found),
      })}
    >
      <div className="search-row">
        <div
          className={classNames('matches-search-input-container', {
            'search-input-focused': isInputFocused,
            'search-input-error': errors.numeric || errors.maxReached,
            'has-tags': !isEmpty(matchIDs),
          })}
        >
          <RenderIf condition={!isEmpty(matchIDs)}>
            <div className="flex">
              <div className="tags-row">
                <Icon
                  icon="search"
                  color={isInputFocused ? '#0044CC' : '#999999'}
                  size={16}
                  className="search-icon"
                />
                {matchIDs.map((matchId) => (
                  <Tag
                    key={`search-match-tag-${matchId}`}
                    minimal
                    intent={checkStatus(matchId)}
                    size="mini"
                    className="match-id-tag"
                    onRemove={() => handleRemoveMatch(matchId)}
                  >
                    <Text variant="body1">{matchId}</Text>
                  </Tag>
                ))}
              </div>
              <div className="automarginLeft">
                <Button
                  text={<FormattedMessage id="containers.gameStatus.clear" />}
                  minimal
                  className="clear-all-btn"
                  onClick={onClear}
                />
              </div>
            </div>
          </RenderIf>
          <div className="input-row">
            <RenderIf condition={isEmpty(matchIDs)}>
              <Icon
                icon="search"
                color={isInputFocused ? '#0044CC' : '#999999'}
                size={16}
                className="search-icon"
              />
            </RenderIf>
            <input
              placeholder={getPlaceholderStr()}
              value={value}
              onChange={(e) => onInputChange(e.target.value)}
              ref={searchInput}
              type="text"
              onKeyDown={handleKeyPress}
              onPaste={handlePaste}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              disabled={loading || equals(length(matchIDs), MAX_MATCHES)}
              name="matchIdInput"
            />
            <RenderIf condition={!!value}>
              <Button
                icon={
                  <Icon
                    icon="cross"
                    color={isInputFocused ? '#0044CC' : '#999999'}
                    size={14}
                  />
                }
                iconOnly
                minimal
                size={14}
                onClick={() => onInputChange('')}
                className="cross-button"
              />
            </RenderIf>
          </div>
        </div>
        <Button
          intent="Primary"
          text="Search"
          onClick={handleSearchClick}
          loading={loading}
          minimal={loading}
          alignText="center"
          className="search-button"
          disabled={(isEmpty(matchIDs) && isEmpty(value)) || loading}
        />
      </div>
      <div className="search-messages">
        <RenderIf condition={errors.numeric}>
          <Text variant="body2" className="mgt-10 color-alizarin">
            <FormattedMessage id="containers.gameStatus.search.error.numericOnly" />
          </Text>
        </RenderIf>
        <RenderIf condition={errors.maxReached}>
          <Text variant="body2" className="mgt-10 color-alizarin">
            <FormattedMessage
              id="containers.gameStatus.search.error.max"
              values={{ maxMatches: MAX_MATCHES }}
            />
          </Text>
        </RenderIf>
        <RenderIf condition={!errors.maxReached}>
          <Text variant="body2" className="mgt-10 color-qs">
            <FormattedMessage
              id="containers.gameStatus.search.hint"
              values={{ maxMatches: MAX_MATCHES }}
            />
          </Text>
        </RenderIf>
      </div>
    </div>
  );
};

export default MatchesSearchBox;
